import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useAuth } from "@/core/hooks/useAuth";
import { useMinisteriosQueLidero } from "@/core/hooks/useMinisterios";
import { useCalendario } from "@/modules/calendario/hooks/useCalendario";
import { AgendaCalendario } from "@/modules/calendario/components/AgendaCalendario";
import { DetalheItemCalendario } from "@/modules/calendario/components/DetalheItemCalendario";
import { CORES_MODULO } from "@/modules/calendario/cores";
import { chaveDia, diasEntre } from "@/shared/lib/datas";
import type { CalendarioModulo, ItemCalendario } from "@/modules/calendario/services/calendarioService";

type Vista = "agenda" | "mes";

const MODULOS: { chave: CalendarioModulo; rotulo: string }[] = [
  { chave: "evento", rotulo: "Eventos" },
  { chave: "reserva", rotulo: "Reservas" },
  { chave: "biblioteca", rotulo: "Biblioteca" },
  // Fase 3 (frontend definitivo, 27/09/2026) — Escala como 4ª origem do
  // Calendário (arquitetura §13): precisa aparecer aqui para ganhar filtro
  // próprio e o pontinho colorido na grade do mês, como as outras 3 origens.
  { chave: "escala", rotulo: "Escala" },
];
const NOMES_MES = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];
const DIAS_SEMANA = ["D", "S", "T", "Q", "Q", "S", "S"];

function inicioDoDia(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
function fimDoDia(d: Date): Date {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}
function somarDias(base: Date, dias: number): Date {
  const x = new Date(base);
  x.setDate(x.getDate() + dias);
  return x;
}

export function Calendario() {
  const { podeGerenciarEventos, podeGerenciarReservas } = useAuth();
  const { data: ministeriosLiderados } = useMinisteriosQueLidero();
  const idsLiderados = useMemo(
    () => new Set((ministeriosLiderados ?? []).map((m) => m.id)),
    [ministeriosLiderados],
  );

  const [vista, setVista] = useState<Vista>("mes");
  const [refData, setRefData] = useState<Date>(() => inicioDoDia(new Date()));
  const [filtros, setFiltros] = useState<Record<CalendarioModulo, boolean>>({
    evento: true,
    reserva: true,
    biblioteca: true,
    escala: true,
  });
  const [diaSelecionado, setDiaSelecionado] = useState<string>(() => chaveDia(new Date().toISOString()));
  const [itemSelecionado, setItemSelecionado] = useState<ItemCalendario | null>(null);

  // Sprint 3 (22/07/2026): pode editar/cancelar um item pelo Calendário quem
  // gerencia o módulo OU lidera o ministério dono do item. As telas de
  // Eventos/Reservas continuam sendo o portão real (RLS + checagem própria);
  // isto só decide se as ações aparecem, para não mostrar botão morto.
  function podeGerir(it: ItemCalendario): boolean {
    if (it.modulo === "reserva") {
      return podeGerenciarReservas || (!!it.ministerioId && idsLiderados.has(it.ministerioId));
    }
    if (it.modulo === "evento") {
      return podeGerenciarEventos || (!!it.ministerioId && idsLiderados.has(it.ministerioId));
    }
    return false;
  }

  const { inicioISO, fimISO, celulas } = useMemo(() => {
    if (vista === "mes") {
      const inicioMes = new Date(refData.getFullYear(), refData.getMonth(), 1);
      const inicioGrade = somarDias(inicioMes, -inicioMes.getDay());
      const cels = Array.from({ length: 42 }, (_, i) => {
        const d = somarDias(inicioGrade, i);
        return { data: d, chave: chaveDia(d.toISOString()), noMes: d.getMonth() === refData.getMonth() };
      });
      return {
        inicioISO: inicioDoDia(inicioGrade).toISOString(),
        fimISO: fimDoDia(somarDias(inicioGrade, 41)).toISOString(),
        celulas: cels,
      };
    }
    const hoje = inicioDoDia(new Date());
    return {
      inicioISO: hoje.toISOString(),
      fimISO: fimDoDia(somarDias(hoje, 44)).toISOString(),
      celulas: [] as { data: Date; chave: string; noMes: boolean }[],
    };
  }, [vista, refData]);

  const { data: itens, isLoading } = useCalendario(inicioISO, fimISO);

  const filtrados = useMemo(
    () => (itens ?? []).filter((it) => filtros[it.modulo]),
    [itens, filtros],
  );

  const itensPorDia = useMemo(() => {
    const mapa = new Map<string, ItemCalendario[]>();
    for (const it of filtrados) {
      // F4-17: evento de mais de um dia entra no mapa em CADA dia que ocupa
      // (não só no dia em que começa), pra marcar/aparecer em todos eles na
      // grade do mês e na lista do dia selecionado.
      const dias = it.fim ? diasEntre(it.inicio, it.fim) : [chaveDia(it.inicio)];
      for (const chave of dias) {
        const lista = mapa.get(chave) ?? [];
        lista.push(it);
        mapa.set(chave, lista);
      }
    }
    return mapa;
  }, [filtrados]);

  const hojeChave = chaveDia(new Date().toISOString());

  function alternarFiltro(chave: CalendarioModulo) {
    setFiltros((f) => ({ ...f, [chave]: !f[chave] }));
  }
  function mudarMes(delta: number) {
    setRefData((d) => new Date(d.getFullYear(), d.getMonth() + delta, 1));
  }
  function irParaHoje() {
    setRefData(inicioDoDia(new Date()));
    setDiaSelecionado(hojeChave);
  }

  return (
    <>
      <main className="mx-auto w-full max-w-3xl px-4 py-6 sm:px-6">
        {/* Barra: mês/navegação + alternador de vista */}
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-1">
            {vista === "mes" && (
              <button
                onClick={() => mudarMes(-1)}
                aria-label="Mês anterior"
                className="grid h-9 w-9 place-items-center rounded-lg text-muted hover:bg-surface-2"
              >
                <ChevronLeft size={18} />
              </button>
            )}
            <span className="min-w-[7rem] text-lg font-bold text-text">
              {vista === "mes"
                ? `${NOMES_MES[refData.getMonth()]} ${refData.getFullYear()}`
                : "Próximos dias"}
            </span>
            {vista === "mes" && (
              <button
                onClick={() => mudarMes(1)}
                aria-label="Próximo mês"
                className="grid h-9 w-9 place-items-center rounded-lg text-muted hover:bg-surface-2"
              >
                <ChevronRight size={18} />
              </button>
            )}
            <button
              onClick={irParaHoje}
              className="ml-1 rounded-lg border border-border bg-surface px-3 py-1.5 text-xs font-semibold text-text hover:bg-surface-2"
            >
              Hoje
            </button>
          </div>

          <div className="flex rounded-lg bg-surface-2 p-1">
            {(["mes", "agenda"] as Vista[]).map((v) => (
              <button
                key={v}
                onClick={() => setVista(v)}
                className={`rounded-md px-4 py-1.5 text-sm font-semibold transition-colors ${
                  vista === v ? "bg-surface text-text shadow-sm" : "text-muted"
                }`}
              >
                {v === "agenda" ? "Agenda" : "Mês"}
              </button>
            ))}
          </div>
        </div>

        {/* Filtros por módulo */}
        <div className="mb-4 flex flex-wrap gap-2">
          {MODULOS.map((m) => {
            const ativo = filtros[m.chave];
            const cor = CORES_MODULO[m.chave];
            return (
              <button
                key={m.chave}
                onClick={() => alternarFiltro(m.chave)}
                className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                  ativo ? "border-transparent bg-surface-2 text-text" : "border-border text-muted opacity-60"
                }`}
              >
                <span className={`h-2 w-2 rounded-full ${cor.ponto}`} />
                {m.rotulo}
              </button>
            );
          })}
        </div>

        {isLoading ? (
          <p className="py-10 text-center text-sm text-muted">Carregando...</p>
        ) : vista === "agenda" ? (
          <AgendaCalendario
            itens={filtrados}
            vazio="Nada agendado nos próximos dias."
            onSelecionar={setItemSelecionado}
          />
        ) : (
          <>
            <div className="overflow-hidden rounded-2xl border border-border bg-surface">
              <div className="grid grid-cols-7 border-b border-border">
                {DIAS_SEMANA.map((d, i) => (
                  <div key={i} className="py-2 text-center text-[11px] font-bold text-muted">
                    {d}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7">
                {celulas.map((c) => {
                  const doDia = itensPorDia.get(c.chave) ?? [];
                  const modulosNoDia = MODULOS.filter((m) => doDia.some((it) => it.modulo === m.chave));
                  const ehHoje = c.chave === hojeChave;
                  const selecionado = c.chave === diaSelecionado;
                  return (
                    <button
                      key={c.chave}
                      onClick={() => setDiaSelecionado(c.chave)}
                      className={`flex aspect-square flex-col items-center justify-start gap-1 border-b border-r border-border pt-1.5 ${
                        selecionado ? "bg-surface-2" : ""
                      }`}
                    >
                      <span
                        className={`grid h-7 w-7 place-items-center rounded-full text-xs font-semibold ${
                          ehHoje ? "bg-primary text-on-primary" : c.noMes ? "text-text" : "text-muted opacity-40"
                        }`}
                      >
                        {c.data.getDate()}
                      </span>
                      <span className="flex h-1.5 gap-0.5">
                        {modulosNoDia.map((m) => (
                          <span key={m.chave} className={`h-1.5 w-1.5 rounded-full ${CORES_MODULO[m.chave].ponto}`} />
                        ))}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-5">
              <AgendaCalendario
                itens={itensPorDia.get(diaSelecionado) ?? []}
                vazio="Nenhum item neste dia."
                onSelecionar={setItemSelecionado}
              />
            </div>
          </>
        )}
      </main>

      <DetalheItemCalendario
        item={itemSelecionado}
        podeGerir={itemSelecionado ? podeGerir(itemSelecionado) : false}
        onClose={() => setItemSelecionado(null)}
      />
    </>
  );
}
